// Zentrale Cache-Key-Konvention. Kein Hook erfindet eigene Keys — alle greifen hierher.
// Struktur: [scope, ...selectors]. Der scope ist ein String-Literal, damit
// Invalidation ueber ganze Domains funktioniert: queryClient.invalidateQueries({ queryKey: ["customer"] }).

export const queryKeys = {
  me: () => ["me"] as const,
  // MCP-1: «Persönlicher Assistent» — eigene Verbindungen + Zustimmungs-Seite.
  assistent: () => ["assistent"] as const,
  assistentAnfrage: (suche: string) => ["assistent", "anfrage", suche] as const,
  assistentUebersicht: () => ["assistent", "uebersicht"] as const,
  users: () => ["users"] as const,
  // LEAD-15: der PL-Pool aus app.pl_pool() — unter «users», damit eine
  // Invalidation der Nutzerliste ihn mitnimmt.
  plPool: () => ["users", "pl-pool"] as const,
  team: () => ["team"] as const,
  // G-7: die sechs PL-Signale (scope self = eigene Zahlen auf /verdienst).
  teamPuls: (scope: "role" | "self") => ["team", "puls", scope] as const,
  customers: {
    all: () => ["customer"] as const,
    detail: (id: string) => ["customer", "detail", id] as const,
    ranked: () => ["customer", "ranked"] as const,
    search: (query: string) => ["customer", "search", query] as const,
    folderSuggestions: (customerId: string) =>
      ["customer", "folder-suggestions", customerId] as const,
    emailSuggestions: (customerId: string) =>
      ["customer", "email-suggestions", customerId] as const,
  },
  projects: {
    all: () => ["project"] as const,
    detail: (id: string) => ["project", "detail", id] as const,
    listByCustomer: (customerId: string) =>
      ["project", "by-customer", customerId] as const,
    events: (projectId: string) => ["project", "events", projectId] as const,
    eventsRecent: (limit: number) =>
      ["project", "events-recent", limit] as const,
    calc: (projectId: string) => ["project", "calc", projectId] as const,
    steps: (projectId: string) => ["project", "steps", projectId] as const,
    // WB-7 Regel 7 — wiederkehrende eigene Schritte, projektuebergreifend.
    katalogKandidaten: () => ["project", "katalog-kandidaten"] as const,
    // ABS-1 — Stand des Abschluss-Vorgangs (Readiness + Berechtigungen).
    completion: (projectId: string) =>
      ["project", "completion", projectId] as const,
    // FB-M3 — Lager-Entnahmen (Material ohne Lieferanten-Beleg).
    stockWithdrawals: (projectId: string) =>
      ["project", "stock-withdrawals", projectId] as const,
    // KALK-7 — die Excel-Kalkulation aus dem Ordner.
    excelCalc: (projectId: string) =>
      ["project", "excel-calc", projectId] as const,
    excelAbgleich: (projectId: string) =>
      ["project", "excel-abgleich", projectId] as const,
    // HOME-13 — Ordner-Drift. Ohne projectId die volle Liste (Badge in der
    // Projektliste), mit projectId der eine Fall (Banner am Detail). Beide
    // teilen den Praefix, damit eine Entscheidung beide Sichten erneuert.
    folderDrift: (projectId?: string) =>
      projectId === undefined
        ? (["project", "folder-drift"] as const)
        : (["project", "folder-drift", projectId] as const),
    // HOME-14 — Dubletten-Beziehung. Ohne projectId die volle Liste (Chip an
    // BEIDEN Zeilen der Projektliste), mit projectId die Beziehungen dieses
    // einen Projekts (Banner am Detail). Gemeinsamer Praefix, damit EINE
    // Entscheidung beide Sichten erneuert — auch die der Gegenseite.
    leadDuplicates: (projectId?: string) =>
      projectId === undefined
        ? (["project", "lead-duplicates"] as const)
        : (["project", "lead-duplicates", projectId] as const),
    // RO-5 — der Dach-Auskunft-Entwurf: Metadaten und die HTML-Vorschau.
    roofDocument: (projectId: string) =>
      ["project", "roof-document", projectId] as const,
    roofDocumentHtml: (projectId: string) =>
      ["project", "roof-document-html", projectId] as const,
    documents: (projectId: string, path: string) =>
      ["project", "documents", projectId, path] as const,
    offerImage: (projectId: string) =>
      ["project", "offer-image", projectId] as const,
    offers: (projectId: string) =>
      ["project", "offers", projectId] as const,
    // OFF-19: Stand der Pruefung einer Offerten-Version, und Sandros Liste.
    offerCheck: (offerId: string) => ["project", "offer-check", offerId] as const,
    offerCheckListe: () => ["project", "offer-check", "liste"] as const,
    leadIntake: (projectId: string) =>
      ["project", "lead-intake", projectId] as const,
    // TRANSPARENZ-3: warum der Vorgang (noch) keinen Kunden hat.
    kundeHinweis: (projectId: string) =>
      ["project", "kunde-hinweis", projectId] as const,
    // TRANSPARENZ-4/-5: wo sich das OS an diesem Vorgang zurueckhaelt.
    osHinweise: (projectId: string) =>
      ["project", "os-hinweise", projectId] as const,
    // PL-UEBERNAHME-1: darf ich uebernehmen / zuruecknehmen / Schritte umstellen.
    uebernahme: (projectId: string) =>
      ["project", "uebernahme", projectId] as const,
    ranked: (opts?: {
      customerId?: string | undefined;
      scope?: "mine" | "all" | undefined;
    }) => {
      const scope = opts?.scope ?? "all";
      return opts?.customerId
        ? (["project", "ranked", scope, opts.customerId] as const)
        : (["project", "ranked", scope] as const);
    },
    // W12-2: Praefix ueber ALLE ranked-Varianten (scope × customerId). Der
    // optimistische Patch nach einer Lead-Aktion muss jede gecachte Sicht
    // treffen, nicht nur die gerade offene.
    rankedAll: () => ["project", "ranked"] as const,
    // WB-1: die Startseite der Projektleitung — Band, Kacheln, Warteliste
    // und Anfragen-Board in EINER Antwort. Kein Scope im Schluessel: die
    // Werkbank ist immer der Arbeitsplatz des angemeldeten Menschen.
    werkbank: () => ["project", "werkbank"] as const,
    // G-4: Scope im Key — die CEO-Home laedt mine («Meine Projekte») und all
    // (Firmen-Pipeline) als getrennte Queries. WB-4: `withActive` ist weg,
    // die laufenden Projekte haengen an `werkbank()`.
    lifecycle: (opts?: { scope?: "mine" | "all" | undefined }) =>
      opts?.scope === undefined
        ? (["project", "lifecycle"] as const)
        : (["project", "lifecycle", opts.scope] as const),
  },
  invoices: {
    all: () => ["invoice"] as const,
    listByProject: (projectId: string) =>
      ["invoice", "by-project", projectId] as const,
    listByCustomer: (customerId: string) =>
      ["invoice", "by-customer", customerId] as const,
    listAll: (scope: "all" | "open" | "overdue") =>
      ["invoice", "list-all", scope] as const,
    suggestionsByCustomer: (customerId: string) =>
      ["invoice", "suggestions", customerId] as const,
  },
  bills: {
    all: () => ["bill"] as const,
    listByProject: (projectId: string) =>
      ["bill", "by-project", projectId] as const,
    openByVendor: (vendorNameNormalized: string, excludeId: string) =>
      ["bill", "open-by-vendor", vendorNameNormalized, excludeId] as const,
    vendorItemHints: (vendorNameNormalized: string) =>
      ["bill", "vendor-item-hints", vendorNameNormalized] as const,
    // FB-M4: Kosten-Abschnitt der Projektseite.
    suggestionsForProject: (projectId: string) =>
      ["bill", "suggestions-for-project", projectId] as const,
    searchUnassigned: (query: string) =>
      ["bill", "search-unassigned", query] as const,
    allocationsByProject: (projectId: string) =>
      ["bill-allocations", "by-project", projectId] as const,
  },
  drilldown: {
    all: () => ["drilldown"] as const,
    kpi: (kpiId: string, filters: Readonly<Record<string, string>>) => {
      const keys = Object.keys(filters).sort();
      const parts = keys.map((k) => `${k}=${filters[k]}`);
      return ["drilldown", kpiId, ...parts] as const;
    },
  },
  earnings: {
    all: () => ["earnings"] as const,
    years: () => ["earnings", "years"] as const,
    overview: (year: number) => ["earnings", "overview", year] as const,
    // BONUS-1 A: die Uebersicht «Bonus <Jahr>» (unter `earnings.all`, damit
    // jede Freigabe/Verteilung sie mit invalidiert).
    bonusJahr: (year: number) => ["earnings", "bonus-jahr", year] as const,
    // BONUS-1 B: die Karte «DB verteilen» eines Projekts.
    bonusVerteilung: (projectId: string) =>
      ["earnings", "bonus-verteilung", projectId] as const,
    detail: (year: number, userId: string) =>
      ["earnings", "detail", year, userId] as const,
  },
  activity: {
    all: () => ["activity"] as const,
    timeline: (filters: {
      period: string;
      userId?: string | undefined;
      action?: string | undefined;
      limit?: number | undefined;
    }) =>
      [
        "activity",
        "timeline",
        filters.period,
        filters.userId ?? null,
        filters.action ?? null,
        filters.limit ?? null,
      ] as const,
    stats: (period: string) => ["activity", "stats", period] as const,
  },
  adminSettings: () => ["admin-settings"] as const,
  homeKpis: () => ["home-kpis"] as const,
  revenueComparison: (year?: number) =>
    year === undefined
      ? (["revenue-comparison"] as const)
      : (["revenue-comparison", year] as const),
  erfolgsrechnung: (year?: number) =>
    year === undefined
      ? (["erfolgsrechnung"] as const)
      : (["erfolgsrechnung", year] as const),
  products: {
    all: () => ["products"] as const,
    list: () => ["products", "list"] as const,
    search: (q: string, group?: string) =>
      ["products", "search", q, group ?? null] as const,
    // OFF-19: Vergleichsbasis + Sortimentspreise der Offerten-Pruefung. Unter
    // `products`, damit jede Preis-Aenderung im Sortiment sie mit abraeumt.
    offerCheck: () => ["products", "offer-check"] as const,
  },
  // Preisgesetz-Bedienung (PS-10): Config + Undo-Status der letzten Preisrunde.
  priceLaw: {
    all: () => ["price-law"] as const,
    config: () => ["price-law", "config"] as const,
    undoStatus: () => ["price-law", "undo-status"] as const,
  },
  // Live-Faktoren der Offerten-Engine (PS-11) — Konfigurator-Previews +
  // Client-erzeugte Calc-Wahrheit (TruckCharge/Kombi-Emit).
  enginePricing: () => ["engine-pricing"] as const,
  notifications: {
    all: () => ["notifications"] as const,
    list: () => ["notifications", "list"] as const,
  },
  leads: {
    all: () => ["leads"] as const,
    dashboard: () => ["leads", "dashboard"] as const,
    // LEAD-2/LEAD-3: die «Wer uebernimmt?»-Kachel auf der Startseite.
    unassigned: () => ["leads", "unassigned"] as const,
    // LEAD-23: die Arbeitsliste hinter dem Menuepunkt «Leads».
    open: () => ["leads", "open"] as const,
    // LEAD-27: unter `leads`, damit Wegraeumen/Zurueckholen sie mit abraeumt.
    ablage: () => ["leads", "ablage"] as const,
  },
  portalSessions: {
    all: () => ["portal-sessions"] as const,
    listByProject: (projectId: string) =>
      ["portal-sessions", "by-project", projectId] as const,
  },
  portalOrders: {
    all: () => ["portal-orders"] as const,
    detail: (id: string) => ["portal-orders", "detail", id] as const,
    byProject: (projectId: string) =>
      ["portal-orders", "by-project", projectId] as const,
    existsByProjects: (projectIds: readonly string[]) =>
      ["portal-orders", "exists-by-projects", [...projectIds].sort()] as const,
  },
  insights: {
    home: () => ["insights", "home"] as const,
    customer: (customerId: string) =>
      ["insights", "customer", customerId] as const,
    project: (projectId: string) =>
      ["insights", "project", projectId] as const,
  },
  // NEU-1 «Was ist neu»: das Fenster, die volle Liste, die Kommandostand-Sicht.
  // Keine Nutzer-Kennung im Key — die Identitaet haelt cacheIdentity.ts.
  releaseNotes: {
    fenster: () => ["release-notes", "fenster"] as const,
    liste: () => ["release-notes", "liste"] as const,
    kommandostand: () => ["release-notes", "kommandostand"] as const,
  },
  // FRAGEN-1: was die Antwort traegt, haengt am PAAR Anmelder + Vertretener
  // (Memory `workflow_frontend_cache_identity`) — beide stehen im Schluessel.
  fragebogen: {
    ansicht: (kennung: string, userId: string | null, actedAsUserId: string | null) =>
      ["fragebogen", kennung, userId, actedAsUserId] as const,
  },
  // NACHFASS-2: die Seite hinter den Knoepfen der Nachfass-Mail — am PAAR
  // Anmelder + Vertretener wie der Fragebogen.
  nachfassen: {
    fall: (projectId: string, userId: string | null, actedAsUserId: string | null) =>
      ["nachfassen", projectId, userId, actedAsUserId] as const,
  },
  admin: {
    auditLog: (filters: {
      entity?: string;
      actorId?: string;
      action?: string;
      includeSelfTests?: boolean;
      limit: number;
    }) =>
      [
        "admin",
        "audit-log",
        filters.entity ?? null,
        filters.actorId ?? null,
        filters.action ?? null,
        filters.includeSelfTests ?? false,
        filters.limit,
      ] as const,
    diagnostics: () => ["admin", "diagnostics"] as const,
    kommandostand: () => ["admin", "kommandostand"] as const,
    healthTrends: (hours: number) =>
      ["admin", "health-trends", hours] as const,
    bexioOutbox: (filters: {
      status?: string;
      entity?: string;
      pushType?: string;
      limit: number;
    }) =>
      [
        "admin",
        "bexio-outbox",
        filters.status ?? null,
        filters.entity ?? null,
        filters.pushType ?? null,
        filters.limit,
      ] as const,
  },
} as const;
